import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { eventsAPI, authAPI } from '../api';
import { useTheme } from '../ThemeContext';
import ThemeToggle from '../components/ThemeToggle';
import UpgradeModal from '../components/UpgradeModal';
import Sidebar from '../components/Sidebar';
import CreateEventModal from '../components/CreateEventModal';
import TrialBanner from '../components/TrialBanner';
import { useTrial } from '../hooks/useTrial';

import { List, Clock, Calendar, CheckCircle, BarChart2, Settings, LogOut } from 'react-feather';
import './Events.css';

function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { trialStatus, upgradeNow } = useTrial();
  const [visibleQRs, setVisibleQRs] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('');
  const [currentTier, setCurrentTier] = useState('');
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState('all');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();

  // Get venue name from localStorage
  const venueName = localStorage.getItem('venueName') || 'Venue';

  const logoUrl = theme === 'light' 
    ? 'https://storage.googleapis.com/msgsndr/oNgjqmOcyIwtcMEEfqUj/media/69051e7ade8283bc1015dd30.webp'
    : 'https://storage.googleapis.com/msgsndr/oNgjqmOcyIwtcMEEfqUj/media/69051e7a306550caec6c2c16.webp';

  useEffect(() => {
    fetchEvents();
    
    // Check if user is super admin
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    console.log('🔍 Events.js userData:', userData);
    const isSuperAdminUser = userData.role === 'super_admin';
    console.log('🔍 Events.js isSuperAdminUser:', isSuperAdminUser);
    setIsSuperAdmin(isSuperAdminUser);
    
    // Fetch clients if super admin
    if (isSuperAdminUser) {
      fetchClients();
    }
    
    // Check URL params for clientId (from Clients page)
    const clientId = searchParams.get('clientId');
    if (clientId) {
      setClientFilter(clientId);
      setShowCreateEventModal(true);
    }
  }, [searchParams]);

  // Refetch events when client filter changes
  useEffect(() => {
    if (isSuperAdmin) {
      fetchEvents();
    }
  }, [clientFilter]);

  const fetchClients = async () => {
    try {
      const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch('https://live-event-photos.onrender.com/api/admin/clients', {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      const data = await response.json();
      setClients(data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  // Listen for sidebar collapse changes
  useEffect(() => {
    const handleStorageChange = () => {
      setSidebarCollapsed(localStorage.getItem('sidebarCollapsed') === 'true');
    };
    window.addEventListener('storage', handleStorageChange);
    const interval = setInterval(handleStorageChange, 100);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await eventsAPI.getAll(clientFilter);
      
      // Transform events to handle snake_case fields and date strings
      const transformedEvents = response.data.events.map(event => ({
        id: event.id,
        venueId: event.venue_id,
        name: event.name,
        startTime: parseInt(event.start_time, 10),
        endTime: parseInt(event.end_time, 10),
        qrCode: event.qr_code,
        status: event.status,
        createdAt: parseInt(event.created_at, 10)
      }));
      
      setEvents(transformedEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewPhotos = (eventId) => {
    navigate(`/events/${eventId}/photos`);
  };

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await eventsAPI.delete(eventId);
        fetchEvents(); // Refresh list
      } catch (error) {
        console.error('Error deleting event:', error);
        alert('Failed to delete event');
      }
    }
  };

  const toggleQRVisibility = (eventId) => {
    setVisibleQRs(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  const getEventStatus = (event) => {
    const now = Date.now();
    if (now < event.startTime) return 'upcoming';
    if (now > event.endTime) return 'ended';
    return 'active';
  };

  const downloadQRCode = (qrCode, eventName) => {
    const link = document.createElement('a');
    link.href = qrCode;
    link.download = `${eventName}-QR-Code.png`;
    link.click();
  };
  const handleCreateEvent = async () => {
    try {
      const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
      
      // Check if this is an admin user (has adminToken)
      if (token && sessionStorage.getItem('adminToken')) {
        // Check subscription limits
        const response = await fetch('https://live-event-photos.onrender.com/api/admin/can-create-event', {
          headers: {
            'Authorization': `Bearer ${token}`
          },
        });
        
        const data = await response.json();
        
        if (!data.canCreate) {
          setUpgradeReason(data.reason || 'You have reached your event limit');
          setCurrentTier(data.currentTier || 'free');
          setShowUpgradeModal(true);
          return;
        }
      }
      
      setShowCreateEventModal(true);
    } catch (error) {
      console.error('Error checking event limit:', error);
      setShowCreateEventModal(true);
    }
  };

  const handleEventCreated = () => {
    // Refresh events list
    fetchEvents();
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('venueName');
    sessionStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    navigate('/login');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      await authAPI.changePassword(currentPassword, newPassword);
      setPasswordSuccess('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowChangePasswordModal(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (error) {
      setPasswordError(error.response?.data?.error || 'Failed to change password');
    }
  };

  // Filter and search events
  const filteredEvents = events.filter(event => {
    const displayStatus = getEventStatus(event);
    const matchesSearch = event.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = statusFilter === 'all' || displayStatus === statusFilter;
    const matchesClient = clientFilter === 'all' || event.venueId === clientFilter;
    return matchesSearch && matchesFilter && matchesClient;
  });

  // Don't hide Sidebar during loading - show loading state within page content
  // if (loading) {
  //   return (
  //     <div className="loading-screen">
  //       <div className="loading-content">
  //         <div className="spinner"></div>
  //         <p>Loading events...</p>
  //       </div>
  //     </div>
  //   );
  // }

  // Check if user is super admin
  const getUserData = () => {
    const stored = localStorage.getItem('user');
    if (!stored || stored === 'undefined') return null;
    try {
      return JSON.parse(stored);
    } catch (e) {
      return null;
    }
  };

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <ThemeToggle />

      <Sidebar onLogout={handleLogout} isSuperAdmin={isSuperAdmin} />

      <div className="events-content">
        <TrialBanner trialStatus={trialStatus} onUpgrade={upgradeNow} />
        <div className="events-top">
          <h2>Your Events</h2>
          <button 
            onClick={handleCreateEvent} 
            className="btn-primary"
          >
            + Create Event
          </button>
        </div>

        <div className="filters-section">
          <input
            type="text"
            placeholder="Search events by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="upcoming">Upcoming</option>
            <option value="active">Active</option>
            <option value="ended">Ended</option>
          </select>
          {isSuperAdmin && (
            <select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Clients</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.first_name} {client.last_name}
                </option>
              ))}
            </select>
          )}
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading events...</p>
          </div>
        ) : events.length === 0 ? (
          <div className="empty-state">
            <p>No events yet.</p>
            <p>Create your first event to start collecting photos!</p>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="empty-state">
            <p>No events match your search criteria.</p>
            <p>Try adjusting your filters or search term.</p>
          </div>
        ) : (
          <div className="events-grid">
            {filteredEvents.map((event) => {
              const displayStatus = getEventStatus(event);
              const isQRVisible = visibleQRs[event.id];
              
              return (
              <div key={event.id} className="event-card">
                <div className="event-header">
                  <h3>{event.name}</h3>
                  <span className={`status ${displayStatus}`}>
                    {displayStatus.toUpperCase()}
                  </span>
                </div>
                
                <div className="event-times">
                  <p><strong>Start:</strong> {new Date(event.startTime).toLocaleString()}</p>
                  <p><strong>End:</strong> {new Date(event.endTime).toLocaleString()}</p>
                </div>
                
                {event.qrCode && (
                  <div className="qr-section">
                    <button 
                      onClick={() => toggleQRVisibility(event.id)}
                      className="btn-download"
                      style={{ marginBottom: '10px' }}
                    >
                      {isQRVisible ? 'Hide QR Code' : 'Show QR Code'}
                    </button>
                    {isQRVisible && (
                      <>
                        <img src={event.qrCode} alt="Event QR Code" style={{ width: '200px', height: '200px', display: 'block', margin: '10px auto' }} />
                        <button 
                          onClick={() => downloadQRCode(event.qrCode, event.name)}
                          className="btn-download"
                        >
                          Download QR Code
                        </button>
                      </>
                    )}
                  </div>
                )}

                <div className="event-actions">
                  <button onClick={() => handleViewPhotos(event.id)} className="btn-view">
                    Manage Event
                  </button>
                  <button onClick={() => handleDeleteEvent(event.id)} className="btn-delete">
                    Delete
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div className="modal-overlay" onClick={() => setShowChangePasswordModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <h2>Change Password</h2>
            <form onSubmit={handleChangePassword}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="input-field"
                  placeholder="Enter current password"
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-field"
                  placeholder="Enter new password (min 6 characters)"
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-field"
                  placeholder="Confirm new password"
                />
              </div>
              {passwordError && (
                <div style={{ color: '#ef4444', marginBottom: '10px', fontSize: '14px' }}>
                  {passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div style={{ color: '#10b981', marginBottom: '10px', fontSize: '14px' }}>
                  {passwordSuccess}
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowChangePasswordModal(false);
                    setPasswordError('');
                    setPasswordSuccess('');
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        reason={upgradeReason}
        currentTier={currentTier}
      />

      <CreateEventModal
        isOpen={showCreateEventModal}
        onClose={() => setShowCreateEventModal(false)}
        onEventCreated={handleEventCreated}
      />
    </div>
  );
}

export default Events;